import { Input, InputProps } from '@mgmresorts/wcl-lab';
import React, { forwardRef, useMemo, useState } from 'react';
import PhoneInput, { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';

import { useContent } from '../../../../hooks/useContent';
import { BindInput } from '../../../../hooks/useInput';

import StyledContent from './StyledContent';

import 'react-phone-number-input/style.css';

const CustomInput = (props: Partial<InputProps>, ref: React.ForwardedRef<HTMLInputElement>) => {
  const { getContentByPath } = useContent();
  return (
    <Input
      {...props}
      type='text'
      label={getContentByPath('shared.fields.phoneNumber.title')}
      aria-label={getContentByPath('shared.fields.phoneNumber.label')}
      id='phoneNumber'
      ref={ref as React.RefObject<HTMLInputElement>}
    />
  );
};

const ForwardRefCustomInput = forwardRef(CustomInput);

type PhoneNumberInputProps = BindInput & {
  className?: string;
  disabled?: boolean;
  invalidFields?: string[];
  name: string;
  testId?: string;
};

export function PhoneNumberInput({ className = '', name, invalidFields, disabled, ...props }: PhoneNumberInputProps) {
  const { getContentByPath } = useContent();
  const [country, setCountry] = useState('US');

  const error = useMemo(
    (): string | undefined =>
      invalidFields && invalidFields.includes(name) ? getContentByPath('shared.fields.phoneNumber.error') : undefined,
    [getContentByPath, invalidFields, name],
  );

  return (
    <StyledContent data-testid='phone-number-input-wrapper'>
      <PhoneInput
        className={`${className} identity__field ${error ? 'with-error' : ''}`}
        error={!!error}
        hint={error}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        onChange={(value: string) => {
          // The ReactPhoneInput Component returns a string,
          // but the new WCL-Lab Input returns an event,
          // accordingly the useInput hook expects an event as well now.
          if (value && isPossiblePhoneNumber(value)) {
            setCountry(parsePhoneNumber(value)?.country ?? 'US');
          }
          props.onChange({
            target: {
              value,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        }}
        disabled={disabled}
        inputComponent={ForwardRefCustomInput}
        numberInputProps={{
          testId: props.testId,
        }}
        country={country}
        defaultCountry='US'
      />
    </StyledContent>
  );
}
